import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ShoppingCart, User, Menu, X, LogOut, LayoutDashboard } from 'lucide-react';
import { Button } from './ui/button';

export default function Navbar() {
  const { t, language, setLanguage, isRTL } = useLanguage();
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const toggleLanguage = () => {
    const next = language === 'en' ? 'fr' : language === 'fr' ? 'ar' : 'en';
    setLanguage(next);
  };

  return (
    <nav className="fixed w-full z-50 glass-panel border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_algerian-crafts-1/artifacts/a5rfuzx0_High-Resolution-Color-Logo-on-Transparent-Background.png" 
              alt="MadeinDZ" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8 rtl:space-x-reverse">
            <Link to="/" className="text-stone-600 hover:text-primary font-medium">{t('home')}</Link>
            <Link to="/marketplace" className="text-stone-600 hover:text-primary font-medium">{t('marketplace')}</Link>
            <Link to="/contact" className="text-stone-600 hover:text-primary font-medium">{t('contact')}</Link>
          </div>

          {/* Actions */}
          <div className="hidden md:flex items-center space-x-4 rtl:space-x-reverse">
            <Button variant="ghost" onClick={toggleLanguage} className="font-semibold">
              {language.toUpperCase()}
            </Button>

            <Link to="/cart" className="relative p-2 text-stone-600 hover:text-primary">
              <ShoppingCart size={24} />
              {cart.length > 0 && (
                <span className="absolute top-0 right-0 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cart.length}
                </span>
              )}
            </Link>

            {user && user.role === 'admin' && (
              <Link to="/admin">
                <Button variant="destructive" size="sm">
                  Admin Panel
                </Button>
              </Link>
            )}
            {user ? (
              <div className="flex items-center gap-3">
                {user.role === 'seller' && (
                  <Link to="/dashboard">
                    <Button variant="outline" size="sm" className="gap-2">
                      <LayoutDashboard size={16} />
                      {t('dashboard')}
                    </Button>
                  </Link>
                )}
                <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} size="icon">
                  <LogOut size={20} />
                </Button>
              </div>
            ) : (
              <Link to="/auth">
                <Button className="bg-primary hover:bg-orange-700 text-white rounded-full">
                  {t('login')}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
             <Link to="/cart" className="relative p-2 text-stone-600">
              <ShoppingCart size={24} />
              {cart.length > 0 && (
                <span className="absolute top-0 right-0 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cart.length}
                </span>
              )}
            </Link>
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-stone-600">
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-b border-stone-200 p-4 space-y-4">
          <Link to="/" onClick={() => setIsMenuOpen(false)} className="block text-lg font-medium">{t('home')}</Link>
          <Link to="/marketplace" onClick={() => setIsMenuOpen(false)} className="block text-lg font-medium">{t('marketplace')}</Link>
          <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="block text-lg font-medium">{t('contact')}</Link>
          <hr />
          <button onClick={toggleLanguage} className="block text-lg font-medium w-full text-left rtl:text-right">
            Language: {language.toUpperCase()}
          </button>
          {user ? (
            <>
               {user.role === 'seller' && (
                  <Link to="/dashboard" onClick={() => setIsMenuOpen(false)} className="block text-lg font-medium text-primary">
                    {t('dashboard')}
                  </Link>
                )}
               <button onClick={logout} className="block text-lg font-medium text-red-500">{t('logout')}</button>
            </>
          ) : (
             <Link to="/auth" onClick={() => setIsMenuOpen(false)} className="block text-lg font-medium text-primary">{t('login')}</Link>
          )}
        </div>
      )}
    </nav>
  );
}
